import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import DeleteModal from '../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';

const PaymentCurrencyList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [errors, setErrors] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [modalPaymentCurrency, setModalPaymentCurrency] = useState(false);
  const [paymentCurrencyFormData, setPaymentCurrencyFormData] = useState({
    currencyName: null,
    currencyDescription: null,
    currencyCode: null,
    id: null,
  });

  const [filterText, setFilterText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(
          `${api.API_URL}/paymentcurrency/${invoice.id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        toast.success('Payment Currency berhasil dihapus!', {
          autoClose: 3000,
        });
        setDeleteModal(false);
        setInvoice(null);
        fetchData(currentPage, rowsPerPage, token);
      } catch (err) {
        toast.error(`Gagal menghapus payment currency: ${err.message}`, {
          autoClose: 3000,
        });
      }
    }
  };

  const deleteMultiple = () => {
    // Implement multiple delete functionality
    // This would typically involve deleting selected items
    // For now, just close the modal
    setDeleteModalMulti(false);
  };

  const columns = [
    {
      name: (
        <span className='form-label  text-uppercase fw-semibold fs-13'>
          Currency Name
        </span>
      ),
      selector: row => row.currencyName,
      sortable: true,
      width: '35%',
      wrap: true,
      cell: row => {
        return (
          <>
            <h6 className={'mb-0'}>{row.currencyName}</h6>
          </>
        );
      },
    },

    {
      name: (
        <span className='form-label  text-uppercase fw-semibold fs-13'>
          Currency Code
        </span>
      ),
      selector: row => row.currencyCode,
      sortable: true,
      width: '20%',
      wrap: true,
      cell: row => {
        return (
          <>
            <h6 className={'mb-0'}>{row.currencyCode}</h6>
          </>
        );
      },
    },

    {
      name: (
        <span className='form-label  text-uppercase fw-semibold fs-13'>
          Currency Description
        </span>
      ),
      selector: row => row.currencyDescription,
      sortable: true,
      width: '35%',
      wrap: true,
      cell: row => {
        return (
          <>
            <h6 className={'mb-0'}>{row.currencyDescription}</h6>
          </>
        );
      },
    },

    {
      name: (
        <span className='form-label  text-uppercase fw-semibold fs-13'>
          Action
        </span>
      ),
      width: '10%',
      sortable: false,
      className: 'text-end',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-end'>
          <button
            className='btn btn-soft-info btn-sm btn-icon'
            onClick={() => handleUpdateModal(row.id)}
          >
            <i className='ri-pencil-fill align-bottom'></i>
          </button>
          <button
            className='btn btn-soft-danger btn-sm btn-icon'
            onClick={() => handleDeleteModal(row.id)}
          >
            <i className='ri-delete-bin-6-line align-bottom'></i>
          </button>
        </div>
      ),
    },
  ];

  document.title = 'Data Payment Currency';

  const handleChange = e => {
    const { name, value } = e.target;
    setPaymentCurrencyFormData({ ...paymentCurrencyFormData, [name]: value });
  };

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token);
  }, [currentPage, rowsPerPage, token]);

  useEffect(() => {
    // Add some test data if no data is available
    if (data.items.length === 0 && !loading) {
      const testData = [
        {
          id: 1,
          currencyName: 'Indonesian Rupiah',
          currencyCode: 'IDR',
          currencyDescription: 'The official currency of Indonesia',
        },
        {
          id: 2,
          currencyName: 'US Dollar',
          currencyCode: 'USD',
          currencyDescription: 'The official currency of United States',
        },
        {
          id: 3,
          currencyName: 'Euro',
          currencyCode: 'EUR',
          currencyDescription: 'The official currency of European Union',
        },
      ];
      setData({ items: testData });
      setTotalRows(testData.length);
    }
  }, [data.items.length, loading]);

  // Filter data based on search text
  useEffect(() => {
    if (filterText) {
      const filtered = data.items.filter(
        item =>
          item.currencyName?.toLowerCase().includes(filterText.toLowerCase()) ||
          item.currencyCode?.toLowerCase().includes(filterText.toLowerCase()) ||
          item.currencyDescription
            ?.toLowerCase()
            .includes(filterText.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(data.items);
    }
  }, [data.items, filterText]);

  const handleFilterChange = e => {
    setFilterText(e.target.value);
  };

  const clearFilter = () => {
    setFilterText('');
  };

  const fetchData = async (currentPage, rowsPerPage, token) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${api.API_URL}/paymentcurrency?PageNumber=${currentPage}&PageSize=${rowsPerPage}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Handle different possible response structures
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      const totalItems =
        result?.data?.totalItems || result?.totalItems || items.length;

      setData({
        items: Array.isArray(items) ? items : [],
      });
      setTotalRows(totalItems);
    } catch (error) {
      toast.error(`Failed to load data: ${error.message}`);
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setPaymentCurrencyFormData({
      currencyName: null,
      currencyDescription: null,
      currencyCode: null,
      id: null,
    });
    paymentCurrencyModal();
  };

  const handleUpdateModal = (mid = null) => {
    fetchPaymentCurrencyDetail(mid);
    paymentCurrencyModal();
  };

  const paymentCurrencyModal = () => {
    setModalPaymentCurrency(!modalPaymentCurrency);
  };

  const selectedFieldsPaymentCurrency = {
    currencyName: paymentCurrencyFormData.currencyName,
    currencyDescription: paymentCurrencyFormData.currencyDescription,
    currencyCode: paymentCurrencyFormData.currencyCode,
  };

  const handleAddDataPaymentCurrency = async () => {
    try {
      await axios.post(
        `${api.API_URL}/paymentcurrency`,
        selectedFieldsPaymentCurrency,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(`Payment Currency Has been Added`);
    } catch (error) {
      toast.error(`Failed to save data: ${error.message}`);
    } finally {
      fetchData(currentPage, rowsPerPage, token);
    }
  };

  const fetchPaymentCurrencyDetail = async mid => {
    try {
      const response = await axios.get(
        `${api.API_URL}/paymentcurrency/${mid}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      const data = response;
      setPaymentCurrencyFormData({
        currencyName: data.currencyName || null,
        currencyDescription: data.currencyDescription || null,
        currencyCode: data.currencyCode || null,
        id: data.id || null,
      });
    } catch (error) {
      // Handle error silently or show appropriate message
      toast.error(`Failed to fetch payment currency details: ${error.message}`);
    }
  };

  const handleUpdDataPaymentCurrency = async mid => {
    try {
      await axios.put(
        `${api.API_URL}/paymentcurrency/${mid}`,
        selectedFieldsPaymentCurrency,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(`Data Has been Updated`);
    } catch (error) {
      toast.error(`Failed to save data: ${error.message}`);
    } finally {
      fetchData(currentPage, rowsPerPage, token);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
        />
        <DeleteModal
          show={deleteModalMulti}
          onDeleteClick={() => {
            deleteMultiple();
            setDeleteModalMulti(false);
          }}
          onCloseClick={() => setDeleteModalMulti(false)}
        />
        <Container fluid>
          <BreadCrumb title='View Data' pageTitle='Payment Currency' />

          <Row>
            <Col lg={12}>
              <Card id='PaymentCurrencyList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Data Payment Currency
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <button
                          type='button'
                          className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                          onClick={() => handleNewModal()}
                        >
                          <i className='ri-play-list-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                          <span className='d-none d-sm-inline'>
                            Create New Payment Currency
                          </span>
                          <span className='d-sm-none'>New</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Filter Section */}
                  <div className='d-flex justify-content-between align-items-center mt-3'>
                    <div className='d-flex align-items-center gap-2'>
                      <Input
                        type='text'
                        placeholder='Search payment currencies...'
                        value={filterText}
                        onChange={handleFilterChange}
                        style={{ width: '300px' }}
                        className='form-control-sm'
                      />
                      {filterText && (
                        <Button
                          color='secondary'
                          size='sm'
                          onClick={clearFilter}
                          outline
                        >
                          <i className='ri-close-line'></i> Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div className='table-responsive'>
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={filterText ? filteredData : data.items}
                      pagination
                      paginationServer={!filterText}
                      paginationTotalRows={
                        filterText ? filteredData.length : totalRows
                      }
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      showPaginationBottom={true}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      noDataComponent='No payment currencies found'
                      defaultSortFieldId={1}
                      defaultSortAsc={true}
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                            tableLayout: 'fixed',
                          },
                        },
                        responsiveWrapper: {
                          style: {
                            minHeight: '200px',
                            overflowX: 'hidden',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={modalPaymentCurrency}
            role='dialog'
            autoFocus={true}
            centered
            id='addPaymentCurrencyModal'
            toggle={paymentCurrencyModal}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalPaymentCurrency(!modalPaymentCurrency);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {paymentCurrencyFormData.id !== null
                  ? 'Edit Payment Currency'
                  : 'Add Payment Currency'}
              </span>
            </ModalHeader>

            <ModalBody>
              <div className='d-flex mt-2'>
                <Col sm={12}>
                  <Row>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='currency-name' className='form-label '>
                          Currency Name :
                        </Label>
                        <div className='input-group'>
                          <Input
                            type='text'
                            id='currencyName'
                            name='currencyName'
                            className='form-control'
                            value={paymentCurrencyFormData.currencyName || ''}
                            onChange={e => {
                              handleChange(e);
                              setErrors({
                                ...errors,
                                currencyName: '',
                              });
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='currency-code' className='form-label '>
                          Currency Code :
                        </Label>
                        <div className='input-group'>
                          <Input
                            type='text'
                            id='currencyCode'
                            name='currencyCode'
                            className='form-control'
                            value={paymentCurrencyFormData.currencyCode || ''}
                            onChange={e => {
                              handleChange(e);
                              setErrors({
                                ...errors,
                                currencyCode: '',
                              });
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div>
                        <Label
                          htmlFor='currencyDescription'
                          className='form-label'
                        >
                          Currency Description{' '}
                        </Label>
                        <textarea
                          className='form-control'
                          id='currencyDescription'
                          name='currencyDescription'
                          rows='5'
                          value={
                            paymentCurrencyFormData.currencyDescription || ''
                          }
                          onChange={e => {
                            handleChange(e);
                            setErrors({
                              ...errors,
                              currencyDescription: '',
                            });
                          }}
                        ></textarea>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                color='danger'
                className='btn-label btn-sm right'
                onClick={() => {
                  setModalPaymentCurrency(!modalPaymentCurrency);
                }}
              >
                {' '}
                <i className=' ri-close-circle-line label-icon align-middle fs-16 ms-2'></i>{' '}
                Close
              </Button>
              <Button
                color='primary'
                className='btn-label btn-sm right'
                onClick={() => {
                  setModalPaymentCurrency(!modalPaymentCurrency);
                  paymentCurrencyFormData.id !== null
                    ? handleUpdDataPaymentCurrency(paymentCurrencyFormData.id)
                    : handleAddDataPaymentCurrency();
                }}
              >
                {' '}
                <i className='ri-database-2-line label-icon align-middle fs-16 ms-2'></i>{' '}
                {paymentCurrencyFormData.id !== null ? 'Update' : 'Save'}
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PaymentCurrencyList;
